import type { FC } from 'react';

import styled, { css } from 'styled-components';

import { useRecommendationsCategories } from './hooks/useRecommendationsCategories';
import { useRecommendationsCategoriesTracking } from './hooks/useRecommendationsCategoriesTracking';
import { usePDPContext } from '../../../utilities/context/static/PDPContext';
import { media } from '../core/styles/media';
import type { Button4 as Button } from '~/amplienceTypes/schemas/exported/button-schema';
import type { CategoryRecommendations } from '~/amplienceTypes/schemas/imported/category-recommendations-schema';
import { CategoryTag } from '~/components/plp/categoryTag/CategoryTag';
import { CarouselTitle } from '~/components/shared/core/carousel/CarouselTitle';
import { ColorScheme } from '~/components/unmanaged/styles/BaseStyledModifiers';
import { BaseStyledStyling } from '~/components/unmanaged/styles/BaseStyledStyling';
import { defaultText } from '~/config/defaultText';
import { SiteType } from '~/utilities/graphql/codegen';
import type { Breadcrumb } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  CategoriesWrapper: styled.div`
    ${BaseStyledStyling.BoxSizingBorder}

    position: relative;

    ${({ theme }) => css`
      --recommendation-margin: 0;

      margin-inline: 0 0;

      @media ${({ theme }) => media(theme).greaterThan('sm')} {
        --recommendation-margin: ${theme.size['4']};
      }

      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        --recommendation-margin: ${theme.size['6']};
      }
    `}
  `,

  Tags: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--ds-spacing-3xs, 4px);
    justify-content: flex-start;

    ${({ theme }) => css`
      padding: 0 ${theme.padding.sm} ${theme.padding.lg};
    `}

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      padding-inline: ${({ theme }) => theme.padding.md};
    }

    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        [SiteType.Outlet]: ColorScheme.light,
      })}
  `,
};

export const Categories: FC<CategoryRecommendations> = ({ title, categoryList }) => {
  const {
    product: { staticCategoryPath, breadcrumbs },
  } = usePDPContext();

  const recommendedCategories: Button[] = useRecommendationsCategories({
    staticCategoryPath: staticCategoryPath as string,
    breadcrumbs: breadcrumbs as Breadcrumb[],
    categoryList,
  });
  const { categoriesRef, handleCategoryClick } = useRecommendationsCategoriesTracking();

  if (!recommendedCategories?.length) {
    return null;
  }

  return (
    <S.CategoriesWrapper
      data-testid="recommended-categories"
      data-cs-capture=""
      role="region"
      aria-labelledby="recommended-categories-title"
    >
      <CarouselTitle id="recommended-categories">
        {title || defaultText.recommendationsComponent.categoriesTitle}
      </CarouselTitle>

      <S.Tags ref={categoriesRef}>
        {recommendedCategories.map(
          (button: Button, idx) =>
            button?.url && (
              <CategoryTag {...button} onClick={handleCategoryClick} key={`${button.url}-${idx}`} />
            )
        )}
      </S.Tags>
    </S.CategoriesWrapper>
  );
};
